import type { AppState, Screen } from './app';

/**
 * WHAT EACH SCREEN IS CALLED IN THE ADDRESS BAR, and what a URL is allowed to
 * mean when it is the only thing we have.
 *
 * A PURE MODULE. Nothing here touches window, history or the store — state/app.ts
 * owns all of that. The split is the same one boot.ts was pulled out for: the
 * decisions that matter (a cold /new/summary must not show the buy button; a
 * paid wizard step must not be re-enterable) are decisions about VALUES, and a
 * value can be tested without simulating a browser gesture. See routes.test.ts.
 *
 * WHY THERE IS NO ROUTER DEPENDENCY. This is the whole of it: two tables and a
 * guard, over nine screens that never nest and never render two at once. What
 * react-router would add on top is nested outlets, loaders, lazy segments and a
 * <Link> that this app has no use for — the parent moves through a wizard by
 * pressing buttons that also spend money, so every transition already goes
 * through a handler. What it would NOT add is the guard below, which is the
 * only hard part and would have to be written by hand either way.
 */

/**
 * `boot` deliberately has no path. It is a PHASE, not a place: it lasts from
 * the first paint until boot() has read /config.json and asked MSAL whether
 * anyone is signed in, and during it the URL still has to say whatever the
 * parent asked for — otherwise landOnStart() has nothing left to read and every
 * deep link becomes the gallery.
 */
export type Placeable = Exclude<Screen, 'boot'>;

/**
 * A place in the app, as a URL can express it.
 *
 * The two ids are the only pieces of state that live in the address bar, and
 * both are server-minted identifiers that already travel in API responses and
 * in request paths (`POST /videos/{videoId}/playback-urls`,
 * `GET /commissions/{commissionId}`). Neither is a capability: every route
 * behind them is authorised per-family from the Entra access token, so a URL
 * pasted to somebody else's browser answers 403/404 rather than playing a
 * film. That is what makes them safe to put somewhere a parent can copy.
 */
export type Place = {
  screen: Placeable;
  /** `/watch/<id>`. */
  videoId?: string;
  /** `/making/<id>`. */
  commissionId?: string;
};

/**
 * Child mode's URL.
 *
 * It is NOT a screen and it is not in the table below, because nothing may
 * route TO it: the only way in is a parent setting a PIN, and a path that could
 * turn the lock on would be a way for the child to turn it off again by editing
 * the other half of the same string. `/child` is written by state/app.ts when
 * the lock is on and refused by parsePath() when it is not.
 */
export const CHILD_PATH = '/child';

/**
 * The one table. `/` is the gallery because the gallery IS the parent home —
 * it is where boot() lands a signed-in parent — and a home screen that lives
 * one segment down leaves the site's own root with nothing to serve.
 *
 * `/new/...` rather than `/candidates`, `/name`, `/summary`: they are three
 * steps of one act, they are the three screens the guard below treats as a
 * group, and grouping them in the path is what makes that legible from the
 * address bar. They carry NO identifier — see the note on the guard.
 */
const PATHS: Record<Placeable, string> = {
  signin: '/signin',
  gallery: '/',
  candidates: '/new/pick',
  name: '/new/name',
  summary: '/new/summary',
  commission: '/making',
  library: '/library',
  watch: '/watch',
};

/** The URL for a place. The inverse of parsePath() for every place. */
export function pathFor(place: Place): string {
  const base = PATHS[place.screen];
  if (place.screen === 'watch' && place.videoId) {
    return `${base}/${encodeURIComponent(place.videoId)}`;
  }
  if (place.screen === 'commission' && place.commissionId) {
    return `${base}/${encodeURIComponent(place.commissionId)}`;
  }
  return base;
}

/**
 * What a URL asks for, or null when it asks for nothing this app has.
 *
 * NULL IS NOT AN ERROR, and it must not be rendered as one. A static host with
 * an SPA fallback answers every path with index.html and a 200 (verified on
 * 2026-08-25: `GET https://showling-app.pages.dev/watch/abc` -> 200 text/html),
 * so this function sees typos, stale links and whatever a share sheet mangled.
 * The caller sends all of them to the gallery, which is a screen a parent can
 * use, rather than to a 404 page this app does not have.
 */
export function parsePath(pathname: string): Place | null {
  const parts = pathname.split('/').filter(Boolean).map(decodeSegment);
  if (parts.length === 0) return { screen: 'gallery' };

  const [head, tail, ...rest] = parts;
  if (rest.length) return null;

  if (head === 'new' && tail) {
    const step = (Object.keys(PATHS) as Placeable[]).find(
      (screen) => PATHS[screen] === `/new/${tail}`,
    );
    return step ? { screen: step } : null;
  }
  if (tail) {
    // The two screens that carry an identifier. An empty tail (`/watch/`) is
    // already gone — split+filter drops it — and lands on the bare form below,
    // which the guard turns into the library rather than a screen with nothing
    // to show.
    if (head === 'watch') return { screen: 'watch', videoId: tail };
    if (head === 'making') return { screen: 'commission', commissionId: tail };
    return null;
  }

  const screen = (Object.keys(PATHS) as Placeable[]).find(
    (candidate) => PATHS[candidate] === `/${head}`,
  );
  return screen ? { screen } : null;
}

/** A path segment may be percent-encoded; a malformed one is not ours. */
function decodeSegment(part: string): string {
  try {
    return decodeURIComponent(part);
  } catch {
    return part;
  }
}

/**
 * WHAT A URL BECOMES ONCE THE APP'S ACTUAL STATE IS TAKEN INTO ACCOUNT. This is
 * the guard, and it runs on every arrival: a cold load, a back gesture, a
 * forward press and a pasted link all go through here.
 *
 * THREE THINGS IT EXISTS FOR.
 *
 * 1. MID-WIZARD STATE IS NOT IN THE URL, ON PURPOSE. `/new/pick` means nothing
 *    without the character being made, and the obvious fix — putting the
 *    characterId in the path so a cold load could resume — is the one that must
 *    not be taken: `/new/summary/<characterId>` would put "Make it — 4 credits"
 *    under the thumb of anyone who reloaded, bookmarked or shared that URL, and
 *    the reload is exactly the case where the app has forgotten it already
 *    charged them. So the wizard's paths are opaque, and an arrival without the
 *    state behind it goes to the gallery. It costs a parent who reloads
 *    mid-wizard their half-made character; it cannot cost them money.
 *
 * 2. THE PAID DOOR, WHICH USED TO HAVE A RELOAD-SHAPED HOLE IN IT. The old test
 *    was `PAID_WIZARD.has(wanted) && app.get().commissionId`, and it was
 *    correct only while the page stayed loaded: a reload nulls commissionId
 *    while the history entry survives, so backing onto `/new/summary` after
 *    refreshing found no commission to object to and re-offered the buy button.
 *    Real URLs made that far easier to reach — a bookmark is now a way in.
 *
 *    It is closed by BOTH clauses together, and that is why they are written as
 *    one rule rather than two:
 *
 *      same page load   commissionId is set  -> library
 *      after a reload   characterId is null  -> gallery
 *
 *    There is no third case. The only thing that clears commissionId without
 *    clearing characterId is resetWizard(), which clears both.
 *
 * 3. A SCREEN THAT NEEDS AN ID MUST HAVE ONE. `/watch` and `/making` with
 *    nothing after them are honest URLs to type and useless ones to render, so
 *    they resolve to the library — which is where both of those films actually
 *    are, in flight or finished.
 *
 * Child mode is NOT handled here. It is a lock over the whole app rather than a
 * destination, so state/app.ts refuses to consult a URL at all while it is on.
 */
export function resolve(place: Place, state: AppState): Place {
  // Nothing else is reachable without a session, and the sign-in screen is not
  // somewhere to be once there is one. Both directions matter: the first stops
  // a deep link rendering a screen whose every call 401s, the second stops
  // `/signin` from being a way to look at a signed-out shell on purpose.
  if (!state.signedInAs) return { screen: 'signin' };
  if (place.screen === 'signin') return { screen: 'gallery' };

  switch (place.screen) {
    case 'candidates':
    case 'name':
    case 'summary': {
      if (state.commissionId) return { screen: 'library' };
      // `candidates` is the screen that calls draft() -> POST /characters, so
      // what it needs is the Entry that names what to draw; the two after it
      // work on a character that already exists.
      const carried = place.screen === 'candidates' ? state.entry : state.characterId;
      return carried ? place : { screen: 'gallery' };
    }

    case 'commission': {
      // The URL wins over the store, so a reload of `/making/<id>` resumes the
      // film the parent is waiting for instead of dropping them on the gallery
      // with it nowhere on screen — which is the complaint that put a history
      // stack in this app in the first place.
      const commissionId = place.commissionId ?? state.commissionId ?? undefined;
      return commissionId ? { screen: 'commission', commissionId } : { screen: 'library' };
    }

    case 'watch': {
      const videoId = place.videoId ?? state.watchingId ?? undefined;
      return videoId ? { screen: 'watch', videoId } : { screen: 'library' };
    }

    default:
      return place;
  }
}
